import { pickT } from "@repo/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./portfolio-xray-form.i18n";
import type { ComponentFormProps } from "./types";

export type PortfolioXrayConfig = {
  title?: string;
  description?: string;
  displayMode: string;
  showChart: boolean;
  showTable: boolean;
  /**
   * QP-177 — render existing-vs-modified comparison for followup sessions.
   * Strict opt-in: only explicit `true` enables it. Not for top_holdings.
   */
  enableFollowupComparison?: boolean;
};

type PortfolioXrayFormProps = ComponentFormProps<PortfolioXrayConfig> & {
  hasXRayAccess?: boolean;
};

export function PortfolioXrayForm({ config, onChange, hasXRayAccess }: PortfolioXrayFormProps) {
  const t = useT(T);
  const displayMode = config.displayMode || "asset_allocation";
  const showChartTableOptions = displayMode !== "top_holdings" && displayMode !== "equity_style";

  return (
    <div className="space-y-4">
      {!hasXRayAccess && (
        <div className="text-sm text-warning bg-warning/10 p-3 rounded-md border border-warning/30">
          <p className="font-semibold">{t.xrayAccessRequired}</p>
          <p className="mt-1">{t.xrayAccessMessage}</p>
          <p className="mt-1 text-xs">{t.contactAdmin}</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="xray-title">{t.title}</Label>
        <Input
          id="xray-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="xray-description">{t.description}</Label>
        <Textarea
          id="xray-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="xray-display-mode">
          {t.displayMode} <span className="text-destructive">*</span>
        </Label>
        <Select
          value={displayMode}
          onValueChange={(value) => onChange({ ...config, displayMode: value })}
        >
          <SelectTrigger id="xray-display-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asset_allocation">{t.assetAllocation}</SelectItem>
            <SelectItem value="country_exposure">{t.countryExposure}</SelectItem>
            <SelectItem value="regional_exposure">{t.regionalExposure}</SelectItem>
            <SelectItem value="stock_sector">{t.stockSector}</SelectItem>
            <SelectItem value="equity_style">{t.equityStyle}</SelectItem>
            <SelectItem value="bond_sector">{t.bondSector}</SelectItem>
            <SelectItem value="top_holdings">{t.topHoldings}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.displayModeHelp}</p>
      </div>

      {showChartTableOptions && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="xray-show-chart" className="text-base">
                {t.showChart}
              </Label>
              <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
            </div>
            <Switch
              id="xray-show-chart"
              checked={config.showChart !== false}
              onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="xray-show-table" className="text-base">
                {t.showTable}
              </Label>
              <p className="text-sm text-muted-foreground">{t.showTableHelp}</p>
            </div>
            <Switch
              id="xray-show-table"
              checked={config.showTable || false}
              onCheckedChange={(checked) => onChange({ ...config, showTable: checked })}
            />
          </div>

          {!config.showChart && !config.showTable && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {t.validationError}
            </div>
          )}
        </>
      )}

      {displayMode !== "top_holdings" && (
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="xray-followup-comparison" className="text-base">
              {t.enableFollowupComparison}
            </Label>
            <p className="text-sm text-muted-foreground">{t.enableFollowupComparisonHelp}</p>
          </div>
          <Switch
            id="xray-followup-comparison"
            checked={config.enableFollowupComparison === true}
            onCheckedChange={(checked) =>
              onChange({ ...config, enableFollowupComparison: checked })
            }
          />
        </div>
      )}
    </div>
  );
}

export function validatePortfolioXrayConfig(
  config: PortfolioXrayConfig,
  lang = "en_GB",
): string | null {
  const t = pickT(T, lang);
  const displayMode = config.displayMode || "asset_allocation";
  if (displayMode !== "top_holdings" && displayMode !== "equity_style") {
    if (!config.showChart && !config.showTable) {
      return t.validationErrorLong;
    }
  }
  return null;
}

export function getDefaultPortfolioXrayConfig(): PortfolioXrayConfig {
  return {
    title: "Portfolio Analysis",
    description: "Detailed breakdown of portfolio holdings.",
    displayMode: "asset_allocation",
    showChart: true,
    showTable: true,
  };
}
